
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, AlertTriangle, Info, CheckCheck } from 'lucide-react';
import { Alert } from '@/types';
import { format, parseISO } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface AlertPopoverProps {
  alerts: Alert[];
  onNavigate?: () => void;
  onMarkAsRead?: (alertId: string) => void;
}

const AlertPopover: React.FC<AlertPopoverProps> = ({ alerts, onNavigate, onMarkAsRead }) => {
  const navigate = useNavigate();
  
  // Filter out read alerts for display
  const unreadAlerts = alerts.filter(alert => !alert.isRead);
  
  const handleAlertClick = (alert: Alert) => {
    if (!alert.propertyId) {
      toast.error("Could not locate the property for this alert");
      return;
    }
    
    // Mark the alert as read when clicked
    if (onMarkAsRead && alert.id) {
      onMarkAsRead(alert.id);
    }
    
    if (onNavigate) {
      onNavigate();
    }
    
    // Navigate to the property with appropriate URL parameters based on alert category
    if (alert.category === 'insurance') {
      navigate(`/properties/${alert.propertyId}?openInsurance=true`);
    } else if (alert.category === 'mortgage') {
      navigate(`/properties/${alert.propertyId}?openRefinance=true`);
    } else {
      navigate(`/properties/${alert.propertyId}`);
    }
  };
  
  const handleClearAll = () => {
    if (!onMarkAsRead) return;
    
    // Mark all unread alerts as read
    unreadAlerts.forEach(alert => {
      if (alert.id) {
        onMarkAsRead(alert.id);
      }
    });
    
    toast.success("All alerts marked as read");
  };
  
  // Calculate unread alerts count
  const unreadCount = unreadAlerts.length;
  
  const getAlertIcon = (type: 'critical' | 'warning' | 'info') => {
    switch (type) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-destructive shrink-0" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
    }
  };
  
  // Sort alerts by priority (critical first, then warning, then info)
  const sortedAlerts = [...unreadAlerts].sort((a, b) => {
    const priorityOrder = { critical: 0, warning: 1, info: 2 };
    return priorityOrder[a.type as keyof typeof priorityOrder] - priorityOrder[b.type as keyof typeof priorityOrder];
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          aria-label="View alerts"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-destructive text-[10px] text-white flex items-center justify-center translate-x-1 -translate-y-1">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Alerts & Reminders</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge variant="outline">{unreadCount} {unreadCount === 1 ? 'Alert' : 'Alerts'}</Badge>
            )}
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearAll}
                className="h-6 px-2 text-xs"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
        
        {unreadCount === 0 ? (
          <div className="py-6 text-center">
            <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No unread alerts</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {sortedAlerts.map((alert) => (
                <div 
                  key={alert.id} 
                  onClick={() => handleAlertClick(alert)}
                  className="p-3 border rounded-md cursor-pointer hover:bg-accent transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">
                      {getAlertIcon(alert.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-muted-foreground">
                          Due: {format(parseISO(alert.dueDate), 'dd MMM yyyy')}
                        </p>
                        {alert.category && (
                          <Badge 
                            variant={
                              alert.type === 'critical' ? "destructive" : 
                              alert.type === 'warning' ? "warning" : 
                              "info"
                            }
                            className="text-[10px] px-1 py-0"
                          >
                            {alert.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default AlertPopover;
